import { ChangeDetectorRef, Component, EventEmitter, Input, OnInit, Output, ViewChild, ViewEncapsulation, inject, DestroyRef, ChangeDetectionStrategy } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import {Alert, StatementTrackerService} from '../../../statement-tracker/statement-tracker.service';
import {ModalComponent} from '@appkit4/angular-components/modal';
import { take, zip } from 'rxjs';
import { HttpErrorResponse } from '@angular/common/http';
import {DSTStatement} from '../../models/dst-statement.model';
import {SaveActionResponse} from '../../../../shared/_models/save-action-response.model';
import {DSTSharedService} from '../../dst-shared.service';
import {StmtIdBtnService} from '../stmt-id-btn/stmt-id-btn.service';

import { safeJsonInput } from '../../../../shared/_utils/safe-json-input';
@Component({
    selector: 'dst-project-tagging',
    templateUrl: './project-tagging.component.html',
    styleUrls: ['./project-tagging.component.scss'],
    encapsulation: ViewEncapsulation.None,
    standalone: false,
  changeDetection: ChangeDetectionStrategy.Eager,
})
export class ProjectTaggingComponent implements OnInit {
  private readonly destroyRef = inject(DestroyRef);


  @Input() rowObject!: DSTStatement;
  @Output() afterModalClosed: EventEmitter<null>;

  @ViewChild('baseModal', {static: false}) baseModal!: ModalComponent;

  projects!: ProjectAndSteps[];
  businesses!: Business[];

  projectsTreeData: any[] = [];
  businessesTreeData: any[] = [];
  filteredProjectsData: any[] = [];
  filteredBusinessesData: any[] = [];

  selectedProject: ProjectAndSteps | null;
  selectedBusiness: Business | null;
  loadingData: boolean;
  savingSelection: boolean;
  alert!: Alert;

  constructor(private dstSharedSerivce: DSTSharedService,
              private stmtIdBtnService: StmtIdBtnService,
              private statementTrackerService: StatementTrackerService,
              private cdr: ChangeDetectorRef) {
    this.afterModalClosed = new EventEmitter<null>();
    this.selectedBusiness = null;
    this.selectedProject = null;
    this.loadingData = false;
    this.savingSelection = false;
  }

  ngOnInit(): void {
    this.alert = this.dstSharedSerivce.alert;

    this.projects = [];
    this.businesses = [];
    const loadProjects$ = this.statementTrackerService.loadProjects(this.rowObject?.TAXYEAR.toString());
    const loadBusinessTiers$ = this.statementTrackerService.loadBusinessTiers(this.rowObject?.TAXYEAR.toString());
    this.loadingData = true;
    zip(loadProjects$, loadBusinessTiers$).pipe(take(1))
      .subscribe((data: [ProjectsAndStepsResponse[], BusinessResponse[]]) => {
        try {
          this.projects.push({id: -1, name: 'None'});
          data[0].forEach((p: ProjectsAndStepsResponse) => {
            this.projects.push(safeJsonInput<any>(p.PROJECTSANDSTEPS, null, 'project-tagging.parse1'));
          });
          data[1].forEach((b: BusinessResponse) => {
            this.businesses.push(safeJsonInput<any>(b.BUS_HIERARCHY, null, 'project-tagging.parse2'));
          });
        } catch (e: any) {
          if (e instanceof SyntaxError) {
            console.log('JSON parse Syntax Error: ' + e);
          }
          this.projects = [];
          this.businesses = [];
          this.dstSharedSerivce.showAlert('error', 'Something went wrong while retrieving the projects/business data!');
        }
      }, (e: HttpErrorResponse) => {
        this.dstSharedSerivce.showAlert('error', e.message);
      }).add(() => {
      this.loadingData = false;
      this.projectsTreeData = this.transformToTreeData(this.projects);
      this.filteredProjectsData = [...this.projectsTreeData];
      this.businessesTreeData = this.transformToTreeData(this.businesses);
      this.filteredBusinessesData = [...this.businessesTreeData];
    });
  }

  private transformToTreeData(nodes: any[]): any[] {
    return nodes.map(node => ({
      ...node,
      title: node.name,
      key: String(node.id ?? node.name),
      expanded: true,
      iconOpened: node.children?.length ? 'icon-down-chevron-outline' : undefined,
      iconClosed: node.children?.length ? 'icon-right-chevron-outline' : undefined,
      children: node.children?.length ? this.transformToTreeData(node.children) : undefined
    }));
  }

  filterProjects(filterValue: string): void {
    if (!filterValue || filterValue.trim() === '') {
      this.filteredProjectsData = [...this.projectsTreeData];
    } else {
      this.filteredProjectsData = this.filterTreeNodes(this.projectsTreeData, filterValue);
    }
  }

  filterBusinesses(filterValue: string): void {
    if (!filterValue || filterValue.trim() === '') {
      this.filteredBusinessesData = [...this.businessesTreeData];
    } else {
      this.filteredBusinessesData = this.filterTreeNodes(this.businessesTreeData, filterValue);
    }
  }

  private filterTreeNodes(nodes: any[], filterText: string): any[] {
    const lower = filterText.toLowerCase();
    return nodes.reduce((result: any[], node: any) => {
      const filteredChildren = node.children ? this.filterTreeNodes(node.children, filterText) : [];
      const matchesTitle = (node.title || '').toLowerCase().includes(lower);
      if (matchesTitle || filteredChildren.length > 0) {
        result.push({
          ...node,
          expanded: true,
          children: filteredChildren.length > 0 ? filteredChildren : node.children
        });
      }
      return result;
    }, []);
  }

  private setExpandedRecursive(nodes: any[], expanded: boolean): any[] {
    return nodes.map(node => ({
      ...node,
      expanded,
      children: node.children ? this.setExpandedRecursive(node.children, expanded) : undefined
    }));
  }

  private getProjectFromRow(): ProjectAndSteps {
    if (!this.rowObject.PROJECTID) {
      return {name: ''};
    }
    if (!this.rowObject.projectStepName || this.rowObject.projectStepName === '') {
      return {
        id: this.rowObject.PROJECTID,
        name: this.rowObject.projectName
      };
    } else {
      return {
        id: this.rowObject.projectStepId,
        name: this.rowObject.projectStepName,
        parentProjectId: this.rowObject.PROJECTID,
        parentProjectName: this.rowObject.projectName
      };
    }
  }

  private getBusinessFromRow(): Business {
    if (!!this.rowObject.bslaDesc) {
      return {
        id: this.rowObject.PROJECTBSLACODE,
        name: this.rowObject.bslaDesc
      };
    }
    return {
      name: ''
    };
  }

  showMore(id: string): void {
    this.baseModal.showModal(id);
    this.selectedProject = this.getProjectFromRow();
    this.selectedBusiness = this.getBusinessFromRow();
  }

  closeModal(id: string): void {
    this.baseModal.closeModal(id);
    //this.notifyAfterModalClosed();
  }

  closeModalOk(id: string): void {
    this.baseModal.closeModalOk(id);
   // this.notifyAfterModalClosed();
  }

  modalDetermine(): void {
  }

  modalClose(): void {
    //this.notifyAfterModalClosed();
  }

  private notifyAfterModalClosed(): void {
    this.afterModalClosed.emit();
  }

  private unwrapTreeEvent(event: any): any {
    let item = event?.item;
    while (item && item.title === undefined && item.item !== undefined) {
      item = item.item;
    }
    return item;
  }

  onProjectSelected(event: any): void {
    const item = this.unwrapTreeEvent(event);
    this.selectedProject = item ? {
      id: item.id,
      name: item.name,
      parentProjectId: item.parentProjectId,
      parentProjectName: item.parentProjectName
    } : null;
    this.cdr.detectChanges();
  }

  getProjectName(): string {
    if (!this.selectedProject || this.selectedProject.name === 'None') {
      return '';
    }
    if (this.selectedProject.parentProjectName) {
      return this.selectedProject.parentProjectName;
    }
    return this.selectedProject.name;
  }

  getProjectStepName(): string {
    if (!this.selectedProject || !this.selectedProject.parentProjectName) {
      return '';
    }
    return this.selectedProject.name;
  }

  onBusinessSelected(event: any): void {
    const item = this.unwrapTreeEvent(event);
    this.selectedBusiness = item ? {
      id: item.id,
      name: item.name
    } : null;
    this.cdr.detectChanges();
  }

  OkSubmit(id: string): void {
    this.saveSelection();
    this.baseModal.closeModalOk(id);
  }

  private saveSelection(): void {
    let projectId = '', projectStepId = '', projectBSLACode = '';
    // for business only
    if (this.selectedProject?.name === '') {
      projectBSLACode = this.selectedBusiness!.id!;
    } else if (!this.selectedProject?.parentProjectName) {
      // for project only
      projectId = this.selectedProject!.name === 'None' ? '' : this.selectedProject!.id!.toString();
      projectBSLACode = this.selectedBusiness!.id!;
    } else if (this.selectedProject?.parentProjectName) {
      // for step
      projectId = this.selectedProject!.parentProjectId!.toString();
      projectStepId = this.selectedProject!.id!.toString();
      projectBSLACode = this.selectedBusiness!.id!;
    }
    this.savingSelection = true;
    this.stmtIdBtnService.saveProjectTagging(this.rowObject.statementId, projectId, projectStepId, projectBSLACode).pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe((response: SaveActionResponse) => {
        if (response.callSuccess === '1') {
          this.rowObject.PROJECTID = Number.parseInt(projectId);
          this.rowObject.projectName = this.getProjectName();
          this.rowObject.projectStepId = Number.parseInt(projectStepId);
          this.rowObject.projectStepName = this.getProjectStepName();
          this.rowObject.PROJECTBSLACODE = projectBSLACode;
          //this.rowObject.PROJECTBSLACODE = this.selectedBusiness?.name || '';
          this.dstSharedSerivce.showAlert('success', 'Your project and business data are updated successfully!');
        }
      }, (error: HttpErrorResponse) => {
        console.log(error.message);
        this.dstSharedSerivce.showAlert('error', 'Something went wrong while saving your selection, please try again!');
      }).add(() => {
      this.savingSelection = false;
    });
  }

  collapseAllProjectTrees(): void {
    this.filteredProjectsData = this.setExpandedRecursive(this.filteredProjectsData, false);
  }

  collapseAllBusinessTrees(): void {
    this.filteredBusinessesData = this.setExpandedRecursive(this.filteredBusinessesData, false);
  }

}

interface ProjectsAndStepsResponse {
  PROJECTSANDSTEPS: string;
}

interface BusinessResponse {
  BUS_HIERARCHY: string;
}

interface ProjectAndSteps {
  id?: number | string,
  name: string,
  parentProjectId?: number | string,
  parentProjectName?: string
  children?: ProjectAndSteps[]
}

interface Business {
  id?: string,
  name: string,
  children?: Business[]
}

export interface ApTreeClickEvent {
  item: any;
  event: Event;
  treeElement: HTMLElement;
}
